import { z } from "zod";
import { llmProviderSchema, modelReasoningLevelSchema, runModelConfigSchema } from "./model-config.js";
import { VenusParentBindingSchema } from "./venus-parent-binding.js";
/** Header carrying the caller's own identifier for a model call; reused on the caller's retries. */
export const MODEL_GATEWAY_REQUEST_ID_HEADER = "x-mlx-gateway-request-id";
/** Identity of the work a model call serves, recorded on the gateway receipt. */
export const MODEL_GATEWAY_IDENTITY_HEADERS = {
    tenantSlug: "x-mlx-tenant-slug",
    organisationId: "x-mlx-organisation-id",
    runId: "x-mlx-run-id",
    runAttempt: "x-mlx-run-attempt",
    callRole: "x-mlx-call-role",
};
/** Header on every gateway response naming the receipt it logged for the call. */
export const MODEL_GATEWAY_RECEIPT_HEADER = "x-mlx-gateway-receipt-id";
export const modelGatewayCallerClassValues = ["executor", "tenant", "build-worker", "evaluation"];
export const modelGatewayRequestIdSchema = z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/u);
export const modelGatewayCallRoleSchema = z.string().regex(/^[a-z][a-z0-9_]{0,47}$/u);
/**
 * A provider call's token usage in the billed form (pi-ai's): inputTokens excludes the tokens read
 * from or written to the provider's prompt cache, which cacheReadTokens and cacheWriteTokens count;
 * outputTokens includes reasoningTokens. Null is a count the provider did not report.
 */
export const modelGatewayUsageSchema = z.object({
    inputTokens: z.number().int().nonnegative().nullable(),
    outputTokens: z.number().int().nonnegative().nullable(),
    totalTokens: z.number().int().nonnegative().nullable(),
    cacheReadTokens: z.number().int().nonnegative().nullable(),
    cacheWriteTokens: z.number().int().nonnegative().nullable(),
    reasoningTokens: z.number().int().nonnegative().nullable(),
}).strict();
/**
 * A receipt's usage: the billed form, plus contextInputTokens, every input token the model read
 * (inputTokens + cacheReadTokens + cacheWriteTokens). With prompt caching most of a call's input
 * is cache reads or writes, so inputTokens alone understates what the model saw.
 */
export const modelGatewayReceiptUsageSchema = modelGatewayUsageSchema.extend({
    contextInputTokens: z.number().int().nonnegative().nullable(),
}).strict();
/**
 * Where a failure the model gateway reports came from: `upstream` only when the provider itself
 * failed the call (an HTTP error status from the provider, the provider's connection refused,
 * reset or cut before its reply ended, its host unresolvable or its TLS unverifiable, or no answer
 * in time), as the gateway observed on its own egress to the provider
 * (twin-model-gateway/src/provider_failures.ts); `gateway` for everything the gateway itself
 * refused or failed at (its pools, limits, credentials, settlement, adapters and its own
 * errors). Every error the gateway emits carries it: problem bodies and the
 * `x-mlx-error-origin` / `x-mlx-error-kind` headers, and the `failure` field of a stream's error
 * line. A failure that never reached the gateway carries none, and is MLX's own.
 */
export const modelGatewayErrorOriginValues = ["upstream", "gateway"];
export const modelGatewayErrorOriginSchema = z.enum(modelGatewayErrorOriginValues);
/** A provider's own token for why it ended a reply (Gemini's `finishReason`): a name, never its text. */
export const providerFinishReasonSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/u);
export const modelGatewayReceiptSchema = z.object({
    receiptId: z.string().min(1),
    requestId: z.string().min(1),
    callerClass: z.enum(modelGatewayCallerClassValues),
    route: z.string().min(1),
    pool: z.string().min(1),
    provider: z.string().min(1),
    model: z.string().min(1),
    outcome: z.enum(["completed", "failed", "cancelled", "rejected"]),
    errorCode: z.string().nullable(),
    /** Where a failed call's failure came from (above); null for a call that did not fail. */
    errorOrigin: modelGatewayErrorOriginSchema.nullable(),
    startedAt: z.number().int().nonnegative(),
    completedAt: z.number().int().nonnegative(),
    durationMs: z.number().int().nonnegative(),
    usage: modelGatewayReceiptUsageSchema.nullable(),
    /** The usage scan stopped early; usage is null rather than stale counters. Absent is not a finality guarantee. */
    usageTruncated: z.boolean().optional(),
    providerResponseId: z.string().nullable(),
    /** Why the provider ended a call it stopped itself (`provider_stopped`, below); null otherwise. */
    providerFinishReason: providerFinishReasonSchema.nullable(),
    tenantSlug: z.string().nullable(),
    organisationId: z.string().nullable(),
    runId: z.string().nullable(),
    runAttempt: z.number().int().nonnegative().nullable(),
    callRole: z.string().nullable(),
}).strict();
/** The pinned Gemini model MLX serves; the executor gates tools on it and the gateway serves it. */
export const GOOGLE_DEFAULT_MODEL = "gemini-3.8-flash";
/** Header on gateway calls bounding the provider call: epoch milliseconds. */
export const MODEL_GATEWAY_DEADLINE_HEADER = "x-mlx-deadline-at";
/**
 * Which model a caller wants served. The executor chooses the model (the run's model config, or
 * its instance defaults); the gateway resolves it to an endpoint, credentials and provider policy.
 */
export const modelRouteSelectionSchema = z.object({
    modelConfig: runModelConfigSchema.optional(),
    /** The caller's instance defaults, used where the model config names nothing. */
    defaults: z.object({
        provider: z.string().trim().min(1).max(40).optional(),
        model: z.string().trim().min(1).max(200).optional(),
        reasoning: z.string().trim().min(1).max(20).optional(),
        maxTokens: z.string().trim().min(1).max(12).optional(),
    }).strict().optional(),
    defaultProvider: llmProviderSchema.optional(),
    /** Correlates self-hosted Venus progress with the session that asked for it. */
    sessionId: z.string().trim().min(1).max(200).optional(),
    /** A separately billed credential for one caller role instead of the provider's default. */
    credential: z.enum(["memory_curator"]).optional(),
}).strict();
export const openAIAuthModeValues = ["codex_subscription", "api_key"];
/** A resolved model route as callers see it: model metadata and policy, never credentials. */
export const modelGatewayResolvedRouteSchema = z.object({
    selection: modelRouteSelectionSchema,
    /** Identifies the resolved route; a stream call pinned to it fails if the route changed. */
    routeDigest: z.string().regex(/^[a-f0-9]{64}$/u),
    provider: llmProviderSchema,
    modelId: z.string().min(1),
    /** The pi-ai model descriptor, without its endpoint; a bare model ID when the catalogue has none. */
    model: z.union([z.string().min(1), z.record(z.string(), z.unknown())]),
    /** Whether the gateway holds a credential able to serve this route. */
    hasKey: z.boolean(),
    /** Whether the route could be served with a provider API key (not only a subscription). */
    apiKeyConfigured: z.boolean(),
    /** The model descriptor is server-configured Codex metadata whose output limit the provider manages. */
    providerManagedOutput: z.boolean(),
    reasoning: modelReasoningLevelSchema.optional(),
    maxTokens: z.number().int().positive().optional(),
    openAIAuth: z.object({
        order: z.array(z.enum(openAIAuthModeValues)).min(1),
        apiFallbackMaxTokens: z.number().int().positive().optional(),
    }).strict().optional(),
    /** The bound Venus parent's serving identity; its endpoint stays in the gateway. */
    venusParentBinding: VenusParentBindingSchema.omit({ baseUrl: true }).optional(),
    /** The chat template arguments the gateway sends for this route's thinking policy. */
    chatTemplateKwargs: z.record(z.string(), z.unknown()).optional(),
}).strict();
/**
 * What a stream call is for. Each purpose fixes how options combine and which provider policy the
 * gateway applies: agent turns, context-compaction summaries, native Venus turns and the sealed
 * resolver evaluation.
 */
export const modelGatewayStreamPurposeValues = ["turn", "summary", "venus_native", "resolver_eval"];
const streamOptionFields = {
    temperature: z.number().min(0).max(2).optional(),
    maxTokens: z.number().int().positive().optional(),
    cacheRetention: z.enum(["none", "short", "long"]).optional(),
    sessionId: z.string().min(1).max(200).optional(),
    transport: z.enum(["sse", "websocket", "auto"]).optional(),
    maxRetries: z.number().int().nonnegative().max(10).optional(),
    maxRetryDelayMs: z.number().int().nonnegative().optional(),
    timeoutMs: z.number().int().positive().optional(),
    thinkingBudgets: z.record(z.string(), z.number().int().nonnegative()).optional(),
    serviceTier: z.literal("priority").optional(),
    codexOriginator: z.string().min(1).max(100).optional(),
    codexMaxAlignment: z.boolean().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
};
/** Stream options a caller may set; credentials and endpoints are never among them. */
export const modelGatewayStreamOptionsSchema = z.object(streamOptionFields).strict();
/** The caller's own decisions, applied over the route's options; reasoning passes the route's policy. */
export const modelGatewayStreamOverridesSchema = z.object({
    ...streamOptionFields,
    /** The reasoning this call runs at; "off" runs without reasoning. Defaults to the route's. */
    reasoning: z.enum(["off", "minimal", "low", "medium", "high", "xhigh", "max"]).optional(),
}).strict();
export const modelGatewayStreamPolicySchema = z.object({
    /** Provider prompt-cache routing key; the caller derives it without exposing tenant identity. */
    promptCacheKey: z.string().regex(/^mlx-v1-[a-f0-9]{57}$/u).optional(),
    /** Governed turns: tool forcing and reasoning-summary exposure on the provider request. */
    governed: z.object({
        exposeReasoningSummary: z.boolean(),
        forceToolChoice: z.boolean(),
        requiredToolName: z.string().min(1).max(200).optional(),
    }).strict().optional(),
    /**
     * Hide answer text (and thinking unless preserved) until evidence exists. Applied before
     * subscription failover decides what counts as committed output.
     */
    suppressPreEvidenceText: z.object({ preserveThinking: z.boolean() }).strict().optional(),
    /** Send each final provider request body back as a payload event (evaluation capture). */
    capturePayload: z.boolean().optional(),
    /** Native Venus turns: the call key that token-rate and probability observations carry. */
    venusNative: z.object({ callKey: z.string().min(1).max(200) }).strict().optional(),
    /** The sealed resolver evaluation: the request the gateway must send, and its durable claim. */
    resolverEval: z.object({
        envelope: z.unknown(),
        query: z.string(),
        evidence: z.object({
            json: z.string(),
            sha256: z.string().regex(/^[a-f0-9]{64}$/u),
            wireSha256: z.string().regex(/^[a-f0-9]{64}$/u),
            wireBytes: z.number().int().positive(),
        }).passthrough(),
    }).strict().optional(),
}).strict();
export const modelGatewayStreamRequestSchema = z.object({
    route: modelRouteSelectionSchema,
    routeDigest: z.string().regex(/^[a-f0-9]{64}$/u),
    purpose: z.enum(modelGatewayStreamPurposeValues),
    /** The model the caller's agent is using; it must be the route's model. */
    model: z.object({ id: z.string().min(1), api: z.string().min(1), provider: z.string().min(1) }).passthrough(),
    /** A pi-ai context: system prompt, messages and tool schemas. */
    context: z.object({
        systemPrompt: z.string().optional(),
        messages: z.array(z.unknown()),
        tools: z.array(z.object({ name: z.string().min(1), description: z.string(), parameters: z.unknown() }).passthrough()).optional(),
    }).strict(),
    /** Options from the caller's agent loop, beneath the route's own options. */
    options: modelGatewayStreamOptionsSchema,
    /** The caller's own decisions, over the route's options. */
    overrides: modelGatewayStreamOverridesSchema,
    policy: modelGatewayStreamPolicySchema,
}).strict();
export const VENUS_RUNTIME_PROGRESS_STAGES = [
    "request_preparing",
    "job_submitting",
    "job_queued",
    "job_running",
    "job_completed",
    "job_failed",
    "response_validating",
    "response_ready",
];
/**
 * What an upstream (provider) failure was. A gateway failure's kind is its own error code.
 * `provider_stopped`: the provider answered and then ended its reply itself, for a reason it gave
 * (its `finishReason`, below): Gemini's SAFETY, RECITATION, MALFORMED_FUNCTION_CALL, OTHER, ….
 */
export const upstreamFailureKindValues = [
    "provider_unreachable",
    "provider_stream_interrupted",
    "provider_timeout",
    "provider_unavailable",
    "provider_rate_limited",
    "provider_auth",
    "provider_http_error",
    "provider_stopped",
];
/**
 * The provider failures worth asking again: the provider could not be reached, dropped or did not
 * finish its reply, did not answer in time, was unavailable or busy. A request the provider refused
 * as such (`provider_http_error`: 400, 404, 413, 422, …), a reply it stopped itself
 * (`provider_stopped`) or a refused credential is not: asking again gets the same answer.
 */
export const retryableUpstreamFailureKinds = new Set([
    "provider_unreachable",
    "provider_stream_interrupted",
    "provider_timeout",
    "provider_unavailable",
    "provider_rate_limited",
]);
export const modelGatewayFailureKindSchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u);
export const modelGatewayFailureSchema = z.object({
    origin: modelGatewayErrorOriginSchema,
    kind: modelGatewayFailureKindSchema,
    /** The provider's HTTP status, for an upstream HTTP error. */
    status: z.number().int().min(400).max(599).optional(),
    /** Why the provider ended its reply, for `provider_stopped`. */
    finishReason: providerFinishReasonSchema.optional(),
}).strict();
export const MODEL_GATEWAY_ERROR_ORIGIN_HEADER = "x-mlx-error-origin";
export const MODEL_GATEWAY_ERROR_KIND_HEADER = "x-mlx-error-kind";
/**
 * The upstream kind of a provider's HTTP error status. Of the 4xx, only 408, 425 and 429 (and 409
 * when the provider says it may be retried, `x-should-retry: true`) are worth asking again; the
 * rest are the provider refusing the request itself.
 */
export function upstreamKindForStatus(status, retryHint) {
    if (status === 401 || status === 403)
        return "provider_auth";
    if (status === 429)
        return "provider_rate_limited";
    if (status === 408)
        return "provider_timeout";
    if (status === 425 || (status === 409 && retryHint === true))
        return "provider_unavailable";
    if (status >= 500)
        return "provider_unavailable";
    return "provider_http_error";
}
// pi-ai and the agents runtime carry only a failure's message from the gateway to the executor's
// run, so the executor marks the message with the gateway's envelope where it receives it, and
// reads it back where it records the run's failure. The mark is MLX's own, never provider text: it
// is read only at the very start of the message, or right after the agents runtime's one wrapper
// when that wrapper opens the first parenthesis of the message (its friendly sentence has none), so
// text a provider or a model wrote, which always follows the mark, can never pose as one.
const FAILURE_MARK_PATTERN = /^(?:[^()]{0,300}\(pi-agent message_end error: )?\[mlx-failure origin=(upstream|gateway) kind=([a-z][a-z0-9_]{0,63})(?: status=(\d{3}))?(?: finish=([A-Za-z][A-Za-z0-9_]{0,63}))?\] /u;
/** A failure message marked with the gateway's envelope. */
export function markModelGatewayFailure(failure, message) {
    return `[mlx-failure origin=${failure.origin} kind=${failure.kind}${failure.status ? ` status=${failure.status}` : ""}${failure.finishReason ? ` finish=${failure.finishReason}` : ""}] ${message}`;
}
/** The gateway's envelope a failure message carries, if it carries one. */
export function readModelGatewayFailure(message) {
    const match = message ? FAILURE_MARK_PATTERN.exec(message) : null;
    if (!match)
        return null;
    const status = match[3] ? Number(match[3]) : undefined;
    return { origin: match[1], kind: match[2], ...(status && status >= 400 && status <= 599 ? { status } : {}),
        ...(match[4] ? { finishReason: match[4] } : {}) };
}
